import { ChangeEvent, useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import NotificationProvider from '../../../providers/Notification/NotificationProvider';
import {
  Alert,
  AlertTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { isValidEmail } from '../../../utils/isValidEmail';
import { Box } from '@mui/system';
import {
  DatabaseOfferWithApprovalRequest,
  OfferRejectionReason,
  OfferState,
} from '../../../../../shared/types/offers';
import { updateOfferSheet } from '../../../services/offerSheets';
import { InspectOffer } from '../InspectOffer';
import MessageIcon from '@mui/icons-material/Message';
import GenericContainer from '../../../GenericContainer';
import { colors } from '../../../../../shared/constants';

export interface NewVersionDialogProps {
  offerSheet: DatabaseOfferWithApprovalRequest;
  open: boolean;
  title: string;
  onClose: () => void;
  refetchOffers: () => void;
}

const DataField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography color={'text.secondary'}>{label}</Typography>
      <Typography sx={{ ml: 2 }} color={'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
};

// DirectSale is a closing reason, not a customer rejection reason
const { DirectSale, NoAvailability, Need } = OfferRejectionReason;
const OFFER_CLOSE_REASONS = { DirectSale, NoAvailability, Need };
type OfferCloseReason = keyof typeof OFFER_CLOSE_REASONS;

const ResponsiblePersonDialog = (props: NewVersionDialogProps) => {
  const { offerSheet, open, title, onClose, refetchOffers } = props;
  const { setNotification } = useContext(NotificationProvider);
  const [email, setEmail] = useState('');
  const [closeReason, setCloseReason] =
    useState<OfferCloseReason | null>(null);
  const [actionState, setActionState] = useState(1);
  const handleEmailChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setEmail(event.target.value);
  };

  const validEmail = isValidEmail(email);

  const onClickSaveResponsiblePerson = async (
    offerSheet: DatabaseOfferWithApprovalRequest,
    responsiblePerson: string,
  ) => {
    try {
      await updateOfferSheet({
        id: String(offerSheet.id),
        item: { userEmail: responsiblePerson },
      });
      refetchOffers();
      setNotification({
        type: 'SNACKBAR',
        duration: 3000,
        severity: 'success',
        message: `Tarjouksen ${offerSheet.name} vastuuhenkilö päivitetty onnistuneesti!`,
      });
    } catch (error) {
      setNotification({
        type: 'SNACKBAR',
        duration: 3000,
        severity: 'error',
        message: `Palvelimella tapahtui virhe! Vastuuhenkilön päivittäminen epäonnistui!`,
      });
    } finally {
      setEmail('');
      onClose();
    }
  };

  const onClickCloseOfferRequest = async (
    offerSheet: DatabaseOfferWithApprovalRequest,
  ) => {
    try {
      if (closeReason === null) {
        return;
      }
      await updateOfferSheet({
        id: String(offerSheet.id),
        item: {
          state: OfferState.Closed,
          customerRejectionComment: null,
          customerRejectionReason: closeReason,
          archivedAt: new Date(),
        },
      });
      refetchOffers();
      setNotification({
        type: 'SNACKBAR',
        duration: 3000,
        severity: 'success',
        message: `Tarjous ${offerSheet.name} suljettu onnistuneesti!`,
      });
    } catch (error) {
      setNotification({
        type: 'SNACKBAR',
        duration: 3000,
        severity: 'error',
        message: `Palvelimella tapahtui virhe! Tarjouksen päivittäminen epäonnistui!`,
      });
    } finally {
      setEmail('');
      onClose();
    }
  };

  useEffect(() => {
    return () => {
      setEmail('');
    };
  }, []);
  return (
    <Dialog
      maxWidth={'xl'}
      fullWidth={true}
      disableScrollLock
      open={open}
      onClose={onClose}
    >
      <DialogTitle id="new-version-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {/* <DataField
            label={'Tarjouksen nimi'}
            value={offerSheet.name ?? ''}
          /> */}
          <GenericContainer
            border
            legendTitle="Tarjouspyynnön tiedot"
          >
            <DataField
              label={'Asiakkaan nimi'}
              value={offerSheet.customerName ?? ''}
            />
            <DataField
              label={'Asiakkaan puhelinnumero'}
              value={offerSheet.receiverPhone ?? '045565345'}
            />
            <DataField label={'Ramirentin asiakas'} value={'Kyllä'} />
            <Alert
              severity="info"
              // sx={{ maxWidth: 800 }}
              iconMapping={{
                info: <MessageIcon fontSize="inherit" />,
              }}
            >
              <AlertTitle>Tarjouspyynnön lisätietoja</AlertTitle>
              <i>
                {offerSheet?.lead?.offerRequestInformation ?? '-'}
              </i>
            </Alert>
          </GenericContainer>
          <GenericContainer
            borderColor={colors.ramiOrange}
            border
            legendTitle="Tarjouspohja"
          >
            <InspectOffer
              offerId={String(offerSheet.id)}
              hideBackButton={true}
              hideTitle={true}
              hideFilterBar={true}
              hideContainer={true}
            />
          </GenericContainer>
        </DialogContentText>
        <div style={{ width: '50%' }}>
          <GenericContainer border legendTitle="Toiminto">
            <FormControl sx={{ width: '100%' }}>
              <FormLabel>Valitse toiminto</FormLabel>
              <RadioGroup
                defaultValue={actionState}
                onChange={(event) => {
                  setActionState(Number(event.target.value));
                  setCloseReason(null);
                }}
              >
                <FormControlLabel
                  value={1}
                  control={<Radio />}
                  label="Aseta tarjouspyynnölle vastuuhenkilö"
                />
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label="Sulje tarjouspyyntö (tarjouspyyntöä ei jatketa tarjoukseksi)"
                />
              </RadioGroup>
            </FormControl>
            {actionState === 1 && (
              <TextField
                sx={{ width: '100%', mb: 1, mt: 3 }}
                size="small"
                id="approval-email"
                label="Uusi vastuuhenkilö (sähköpostiosoite)"
                variant="outlined"
                type="email"
                value={email}
                error={
                  !email
                    ? false
                    : !validEmail || !email
                      ? true
                      : false
                }
                helperText={
                  !email
                    ? ''
                    : !validEmail || !email
                      ? 'Varmista että sähköpostiosoite on oikein'
                      : null
                }
                onChange={handleEmailChange}
              />
            )}
            {actionState === 2 && (
              <Stack direction="column" spacing={2} sx={{ ml: 4 }}>
                <FormControl sx={{ width: '100%' }}>
                  <FormLabel>Sulkemisen syy</FormLabel>
                  <RadioGroup
                    name={'closeReason'}
                    value={closeReason}
                    onChange={(e) =>
                      setCloseReason(
                        e.target.value as OfferCloseReason,
                      )
                    }
                  >
                    {Object.entries(OFFER_CLOSE_REASONS).map(
                      ([key, label]) => (
                        <FormControlLabel
                          key={key}
                          value={key}
                          control={<Radio size={'small'} />}
                          label={label}
                        />
                      ),
                    )}
                  </RadioGroup>
                </FormControl>
                <Alert severity="warning">
                  Olet sulkemassa tarjouspyyntöä (Avoin -tilassa oleva
                  tarjous). Tarjouspyyntö suljetaan ja arkistoidaan
                  automaattisesti. Lisäksi tarjouspyynnöstä syntynyt
                  Lead suljetaan Fokuksessa.
                </Alert>
              </Stack>
            )}
          </GenericContainer>
        </div>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-start', ml: 6 }}>
        <Button
          disabled={
            actionState === 1 ? !isValidEmail(email) : !closeReason
          }
          variant="contained"
          onClick={() => {
            if (actionState === 2) {
              onClickCloseOfferRequest(offerSheet);
              // Close it :D
            } else {
              onClickSaveResponsiblePerson(offerSheet, email);
            }
          }}
        >
          Tallenna
        </Button>
        <Button
          color="inherit"
          onClick={() => {
            setEmail('');
            onClose();
          }}
        >
          Peruuta
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResponsiblePersonDialog;
